import { useState, type FormEvent } from "react";
import { TextField } from "../../components/TextField/TextField";
import { Button } from "../../components/Button/Button";
import { InlineNotice } from "../../components/InlineNotice/InlineNotice";
import { createAdminTenant } from "../../lib/api/admin";
import { slugify } from "../../lib/format";
import { isValidSlug, isValidEmail } from "../../lib/validation";
import { isValidHexColor } from "../../lib/colorUtils";
import type { AdminTenant } from "../../types";
import styles from "./CreateTenantForm.module.css";

const DEFAULT_COLOR = "#D95032";

interface CreateTenantFormProps {
  token: string;
  /** `ownerInvitationSent` is true when the owner email had no existing account and was just invited. */
  onCreated: (tenant: AdminTenant, ownerInvitationSent: boolean) => void;
  onCancel?: () => void;
}

export function CreateTenantForm({ token, onCreated, onCancel }: CreateTenantFormProps) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [errors, setErrors] = useState<{ name?: string; slug?: string; color?: string; ownerEmail?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  function handleNameChange(value: string) {
    setName(value);
    if (!slugTouched) setSlug(slugify(value));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const nextErrors: typeof errors = {};
    if (name.trim().length === 0) nextErrors.name = "Enter a business name.";
    if (!isValidSlug(slug)) {
      nextErrors.slug = "Use 2+ lowercase letters, numbers, or hyphens.";
    }
    if (!isValidHexColor(color)) nextErrors.color = "Enter a valid color.";
    if (!isValidEmail(ownerEmail)) nextErrors.ownerEmail = "Enter a valid email address.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const { tenant, ownerInvitationSent } = await createAdminTenant(token, {
        name: name.trim(),
        slug,
        primaryColor: color,
        ownerEmail: ownerEmail.trim(),
      });
      onCreated(tenant, ownerInvitationSent);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Couldn't create the business. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const previewUrl = `${window.location.origin}/join/${slug || "your-business"}`;

  return (
    <div className={styles.card}>
      <h2 className={styles.heading}>Launch a business</h2>
      <p className={styles.body}>
        This opens its signup page, starts its member list, and assigns an owner. Its signup headline and
        welcome message default automatically — the owner can edit them anytime from their Brand kit.
      </p>
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <TextField
          label="Business name"
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          error={errors.name}
          required
        />
        <TextField
          label="Signup slug"
          value={slug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value.toLowerCase());
          }}
          hint="Used in the public signup link."
          error={errors.slug}
          required
        />
        <div>
          <div className={styles.colorRow}>
            <input
              type="color"
              className={styles.colorSwatch}
              value={isValidHexColor(color) ? color : DEFAULT_COLOR}
              onChange={(e) => setColor(e.target.value)}
              aria-label="Brand color"
            />
            <TextField
              label="Brand color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              error={errors.color}
            />
          </div>
        </div>
        <TextField
          type="email"
          label="Owner email"
          value={ownerEmail}
          onChange={(e) => setOwnerEmail(e.target.value)}
          hint="If this email doesn't have an account yet, we'll send an invitation to set one up as the owner."
          error={errors.ownerEmail}
          required
        />
        <p className={styles.preview}>
          Signup link: <strong>{previewUrl}</strong>
        </p>
        {submitError && <InlineNotice tone="error">{submitError}</InlineNotice>}
        <div className={styles.actions}>
          {onCancel && (
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" busy={submitting}>
            {submitting ? "Launching…" : "Launch business"}
          </Button>
        </div>
      </form>
    </div>
  );
}
